import {useState} from 'react';

import {useResetTeenInviteMutation} from '~/store/rtkQuery';
import {AuditLogButton} from '~/features/AuditLog/AuditLogButton';
import {FoundParentSummary} from './FoundParentSummary';
import {useParentLookup} from './useParentLookup';

const RESET_ERROR_MESSAGES: Record<string, string> = {
  PARENT_NOT_FOUND: 'No parent found with that ID.',
  TEEN_NOT_FOUND: 'This teen is no longer claimed on that account.',
  TEEN_NOT_CLAIMED: "This teen's invite isn't currently claimed.",
  TEEN_EMAIL_VERIFIED:
    "This teen has a verified email and can't be reset this way.",
  TEEN_STATE_CHANGED:
    "The teen's state changed during the reset — please try again.",
};

const getResetErrorMessage = (message?: string) => {
  if (!message) {
    return 'Reset failed';
  }
  return RESET_ERROR_MESSAGES[message] ?? message;
};

type ResetState =
  | {status: 'idle'}
  | {status: 'submitting'}
  | {status: 'success'}
  | {status: 'error'; message: string};

export const ResetTeenInvite = () => {
  const {parentLookup, search} = useParentLookup();
  const [resetTeenInvite] = useResetTeenInviteMutation();

  const [query, setQuery] = useState('');
  const [selectedTeenId, setSelectedTeenId] = useState('');
  const [resetState, setResetState] = useState<ResetState>({status: 'idle'});

  const isSearching = parentLookup.status === 'searching';

  const claimedTeens =
    parentLookup.status === 'found'
      ? (parentLookup.parentInfo.teens ?? []).filter(
          (teen): teen is typeof teen & {claimedByUserId: string} =>
            teen.status === 'claimed' && !!teen.claimedByUserId,
        )
      : [];

  const selectedTeen = claimedTeens.find(teen => teen.id === selectedTeenId);

  const onResetInvite = async () => {
    if (
      parentLookup.status !== 'found' ||
      !selectedTeen ||
      resetState.status === 'submitting'
    ) {
      return;
    }

    setResetState({status: 'submitting'});

    const response = await resetTeenInvite({
      parentUserId: parentLookup.parentUserId,
      claimedByUserId: selectedTeen.claimedByUserId,
    });

    setResetState(
      'error' in response
        ? {
            status: 'error',
            message: getResetErrorMessage(response.error?.message),
          }
        : {status: 'success'},
    );
  };

  return (
    <div className="p-5">
      <h1 className="text-2xl font-semibold">Reset Teen Invite</h1>
      <p className="mt-2 text-sm text-gray-600">
        Look up a parent by email or userId and select the claimed teen to reset
        their invite
      </p>

      <div className="mt-4 form-control max-w-md">
        <label className="label" htmlFor="parent-query">
          <span className="label-text font-medium">
            Parent's email or userId
          </span>
        </label>
        <input
          id="parent-query"
          className="input input-bordered w-full"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <button
          className={`btn mt-2 ${
            query.trim() && !isSearching ? 'btn-primary' : 'btn-outline'
          }`}
          onClick={() => search(query)}
          disabled={!query.trim() || isSearching}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {parentLookup.status === 'error' && (
        <div role="alert" className="alert alert-error mt-4 max-w-md">
          <span>{parentLookup.message}</span>
        </div>
      )}

      {parentLookup.status === 'found' && (
        <div className="mt-4 max-w-md rounded-lg bg-base-200 p-3">
          <FoundParentSummary parentLookup={parentLookup} />

          {claimedTeens.length === 0 ? (
            <p className="mt-2 text-red-600">
              This parent has no claimed teens to reset.
            </p>
          ) : (
            <>
              <ul className="mt-3">
                {claimedTeens.map(teen => (
                  <li key={teen.id} className="py-1">
                    <label className="label cursor-pointer justify-start gap-3">
                      <input
                        type="radio"
                        name="claimed-teen"
                        className="radio radio-sm"
                        checked={selectedTeenId === teen.id}
                        onChange={() => setSelectedTeenId(teen.id)}
                      />
                      <span className="text-sm">
                        {teen.teenFirstName} {teen.teenLastName} (
                        {teen.claimedByUserId})
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              <button
                className="btn btn-sm btn-primary mt-2"
                onClick={onResetInvite}
                disabled={!selectedTeen || resetState.status === 'submitting'}>
                {resetState.status === 'submitting'
                  ? 'Resetting...'
                  : 'Reset invite'}
              </button>

              {resetState.status === 'success' && (
                <div role="alert" className="alert alert-success alert-sm mt-2">
                  <span>Invite reset.</span>
                </div>
              )}
              {resetState.status === 'error' && (
                <div role="alert" className="alert alert-error alert-sm mt-2">
                  <span>{resetState.message}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <AuditLogButton endpoints={['POST /parentConfig/resetTeenClaim']} />
    </div>
  );
};
